import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useAtomValue } from 'jotai';
import { reportsAtom } from '@/store/atoms';
import {
  fetchReports, fetchReport, fetchReportHistory,
  createReport, saveReport, publishReport, cloneReport, amendReport, deleteReport,
  fetchContextOptions, fetchReportVisuals, fetchExport,
  fetchTemplates, uploadTemplate, setTemplateActive, deleteTemplate,
  fetchCombinedReports, distributeCombinedReport,
  fetchMe, fetchUserRoles, assignUserRole, removeUserRole, fetchDirectory,
  type SavePayload,
} from './api';
import type { NewReportContext } from './types';

// When VITE_API_BASE_URL is not set, stay in mock/demo mode
const IS_MOCK = !import.meta.env.VITE_API_BASE_URL;

export function useReports() {
  return useQuery({
    queryKey: ['reports'],
    queryFn: fetchReports,
    enabled: !IS_MOCK,
  });
}

export function useReport(id: string) {
  const mockReports = useAtomValue(reportsAtom);
  const mockReport = mockReports.find(r => r.id === id);
  return useQuery({
    queryKey: ['reports', id],
    queryFn: () => fetchReport(id),
    enabled: !IS_MOCK && !!id,
    placeholderData: mockReport
      ? { ...mockReport, sections: [] }
      : undefined,
  });
}

export function useReportHistory(id: string, enabled: boolean) {
  return useQuery({
    queryKey: ['reports', id, 'history'],
    queryFn: () => fetchReportHistory(id),
    enabled: !IS_MOCK && !!id && enabled,
  });
}


export function useCreateReport() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (ctx: NewReportContext) => createReport(ctx),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['reports'] }),
  });
}

export function useSaveReport(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: SavePayload) => saveReport(id, payload),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['reports', id] });
      // Invalidate cached exports — they're stale after a save
      qc.removeQueries({ queryKey: ['exports', id] });
    },
  });
}

export function usePublishReport(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => publishReport(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['reports'] });
      qc.invalidateQueries({ queryKey: ['reports', id] });
    },
  });
}

export function useCloneReport() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, month, year }: { id: string; month: number; year: number }) =>
      cloneReport(id, month, year),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['reports'] }),
  });
}

export function useAmendReport() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => amendReport(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['reports'] }),
  });
}

export function useDeleteReport() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => deleteReport(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['reports'] }),
  });
}

// ─── Report visuals (from stored snapshot) ─────────────────────────────────────

// One round-trip returns every slot's chart-ready data, mapped server-side from
// the report's visual snapshot. Replaces the old per-slot live DAX queries.
export function useReportVisuals(id: string) {
  return useQuery({
    queryKey: ['report-visuals', id],
    queryFn: () => fetchReportVisuals(id),
    enabled: !IS_MOCK && !!id,
    staleTime: Infinity,
    refetchOnWindowFocus: false,
  });
}

export function useExportReport() {
  return useMutation({
    mutationFn: async ({ id, format, filename }: { id: string; format: 'word' | 'pptx'; filename: string }) => {
      const { url } = await fetchExport(id, format);
      const ext = format === 'word' ? 'docx' : 'pptx';
      const a = document.createElement('a');
      a.href = url;
      a.download = `${filename}.${ext}`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    },
  });
}

export function useTemplates() {
  return useQuery({
    queryKey: ['templates'],
    queryFn: fetchTemplates,
    enabled: !IS_MOCK,
    placeholderData: [] as import('./types').ReportTemplate[],
  });
}

export function useUploadTemplate() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ file, kind }: { file: File; kind: import('./types').TemplateKind }) =>
      uploadTemplate(file, kind),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['templates'] }),
  });
}

export function useSetTemplateActive() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, isActive }: { id: string; isActive: boolean }) => setTemplateActive(id, isActive),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['templates'] }),
  });
}

export function useDeleteTemplate() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: deleteTemplate,
    onSuccess: () => qc.invalidateQueries({ queryKey: ['templates'] }),
  });
}

// ─── Combined report decks ─────────────────────────────────────────────────────

export function useCombinedReports() {
  return useQuery({
    queryKey: ['combined'],
    queryFn: fetchCombinedReports,
    enabled: !IS_MOCK,
  });
}

export function useDistributeCombined() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => distributeCombinedReport(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['combined'] });
      // Constituent report statuses changed — refresh the dashboard list too
      qc.invalidateQueries({ queryKey: ['reports'] });
    },
  });
}

// ─── Current user + access management ──────────────────────────────────────────

export function useMe() {
  return useQuery({
    queryKey: ['me'],
    queryFn: fetchMe,
    enabled: !IS_MOCK,
    staleTime: 5 * 60_000,
  });
}

export function useUserRoles() {
  return useQuery({
    queryKey: ['user-roles'],
    queryFn: fetchUserRoles,
    enabled: !IS_MOCK,
  });
}

export function useDirectory() {
  return useQuery({
    queryKey: ['directory'],
    queryFn: fetchDirectory,
    enabled: !IS_MOCK,
    staleTime: 30 * 60_000,
  });
}

export function useAssignUserRole() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: assignUserRole,
    onSuccess: () => qc.invalidateQueries({ queryKey: ['user-roles'] }),
  });
}

export function useRemoveUserRole() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: removeUserRole,
    onSuccess: () => qc.invalidateQueries({ queryKey: ['user-roles'] }),
  });
}

export function useContextOptions() {
  return useQuery({
    queryKey: ['context-options'],
    queryFn: fetchContextOptions,
    enabled: !IS_MOCK,
  });
}
